import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useMatch } from "react-router-dom";
import { type LabelMap, listLabels } from "../api/labelsApi";
import {
	type Lesson,
	type LessonStatus,
	listLessons,
	type RetractableOrg,
} from "../api/lessonsApi";
import { listProjects, type Project } from "../api/projectsApi";
import { Icon } from "../components/Icon";
import { LoadMore } from "../components/LoadMore";
import ProjectFilter from "../components/ProjectFilter";
import { PALETTE } from "../components/palette";
import {
	Chip,
	EmptyState,
	Panel,
	Plate,
	STATUS_ICONS,
	StatusBadge,
} from "../components/ui";
import { When } from "../components/When";
import { describeError } from "../lib/apiErrors";
import { entryKeys, projectEntries } from "../lib/projectEntries";
import "./lessons.css";

// The pool, read by a person. A layout route rather than a page: it fetches
// one page of lessons and holds them, and /lessons/:id renders its detail out
// of that list through the Outlet context - so clicking down the left column
// issues no requests at all. The list returns full bodies for exactly this
// reason. See the design's Section 5.
//
// Selection is a ROUTE and not component state. Making it state would cost the
// back button, deep links, and any ability to paste someone a lesson. It is
// also what lets one breakpoint serve both widths: narrow shows the list at
// /lessons and the detail at /lessons/:id, rather than needing a second layout.

/** What the detail pane reads off the Outlet. */
export interface LessonsContext {
	lessons: Lesson[];
	/**
	 * Which of `lessons` the signed-in account owns, by id.
	 *
	 * The pool returns other people's public lessons once the push tier opens,
	 * and the write path stays owner-scoped, so the detail pane needs this to
	 * decide whether to offer a status control at all. Accumulated across
	 * pages exactly like `lessons`, so the two never disagree about a row.
	 */
	ownedIds: string[];
	/**
	 * Whether any load attempt has ever settled - successfully or not. Not
	 * "the current one has settled": once true it stays true, including
	 * while a later filter's load is still in flight.
	 *
	 * The distinction the detail pane needs is not "is the list empty" but
	 * "has the list been asked yet." Without this, an id that IS in the pool
	 * still fires the fallback fetch on a cold mount, because effects run
	 * child-first and the detail asks before the list has started loading -
	 * which defeats the reason the list returns full bodies at all.
	 *
	 * True even when the load FAILED, so a deep link still falls back to
	 * GET /api/lessons/:id rather than hanging on a pool that never arrived.
	 */
	poolSettled: boolean;
	/**
	 * Write a status the server has already accepted into the loaded page, so
	 * the row and the detail agree without a refetch. Not an optimistic
	 * update - the only caller runs it after the round-trip returns.
	 */
	patchLesson: (id: string, status: LessonStatus) => void;
	/**
	 * The names of the authors of the org lessons on this page, by lesson id.
	 *
	 * Carried beside the documents for the reason `ownedIds` is: a lesson
	 * body is the published contract's shape and nothing server-computed
	 * belongs in it. A key is absent when the author has no name set - the
	 * server does not invent a label, and neither does this page.
	 *
	 * Accumulated across pages exactly like `ownedIds`, so the two never
	 * disagree about a row.
	 */
	authors: Record<string, string>;
	/**
	 * Which org, if any, the signed-in account may retract each org lesson
	 * in, by lesson id.
	 *
	 * The server decides what the caller may act on; this page renders what
	 * it is handed rather than re-deriving ownership or membership. Carried
	 * beside the documents for the reason `authors` is, and accumulated
	 * across pages exactly like it, so the two never disagree about a row.
	 */
	retractableIn: Record<string, RetractableOrg>;
	/**
	 * Which of `lessons` had an author whose account has since been
	 * anonymized, by id.
	 *
	 * Carried beside the documents for the reason `authors` is: a lesson
	 * body is the published contract's shape and nothing server-computed
	 * belongs in it. The label this renders as ("Former member") is this
	 * client's wording, never the server's - a literal in a name field
	 * cannot be told apart from a member who set that as their actual name.
	 *
	 * Accumulated across pages exactly like `authors`, so the two never
	 * disagree about a row.
	 */
	formerAuthors: string[];
	/**
	 * The viewer's names for project keys, as Sessions sets them (ONL-189).
	 *
	 * Read once rather than per page: a label belongs to the viewer, not to a
	 * lesson, so nothing about paging or a filter changes it. Empty until
	 * read and empty if the read fails, and every key then shows as itself.
	 */
	labels: LabelMap;
}

/**
 * Status filtering ships; stack filtering does not.
 *
 * Not a matter of effort. `status` is a real column with a real index, so the
 * server can answer it across the whole pool. Stack lives inside the JSON
 * body, and filtering it in the browser would filter one loaded page and call
 * it the pool - which is wrong the moment a second page exists. Deferred
 * whole rather than shipped shrunk: onlooker-4bw.
 */
const FILTERS: { value: "" | LessonStatus; label: string; empty?: string }[] = [
	// "All" carries no `empty`: an unfiltered pool with nothing in it is the
	// empty POOL, which says something else entirely.
	{ value: "", label: "All" },
	{ value: "active", label: "Active", empty: "No active lessons" },
	{ value: "retracted", label: "Retracted", empty: "No retracted lessons" },
	{ value: "refuted", label: "Refuted", empty: "No refuted lessons" },
	{ value: "superseded", label: "Superseded", empty: "No superseded lessons" },
];

const row = {
	display: "flex",
	gap: "var(--space-3)",
	alignItems: "center",
	padding: "var(--space-3)",
	borderBottom: `2px solid ${PALETTE.border}`,
	textDecoration: "none",
	color: "var(--ink)",
};

export default function LessonsPage() {
	const [lessons, setLessons] = useState<Lesson[] | null>(null);
	// Moves with `lessons` everywhere - replaced on load, appended on
	// loadMore, and left alone by patchLesson, which changes a status and
	// never an owner.
	const [ownedIds, setOwnedIds] = useState<string[]>([]);
	// Moves with `lessons` the same way `ownedIds` does - replaced on load,
	// merged on loadMore, left alone by patchLesson, which changes a status
	// and never an author.
	const [authors, setAuthors] = useState<Record<string, string>>({});
	// Moves with `lessons` the same way `authors` does - replaced on load,
	// merged on loadMore, left alone by patchLesson, which changes a status
	// and never an org's authority.
	const [retractableIn, setRetractableIn] = useState<
		Record<string, RetractableOrg>
	>({});
	// Moves with `lessons` the same way `authors` does - replaced on load,
	// merged on loadMore, left alone by patchLesson, which changes a status
	// and never who anchors a lesson.
	const [formerAuthors, setFormerAuthors] = useState<string[]>([]);
	// Not reset by load(): see the context field's comment.
	const [labels, setLabels] = useState<LabelMap>(new Map());
	const [loadError, setLoadError] = useState<string | null>(null);
	// Whether any load attempt has ever settled - guarded below so a
	// superseded request cannot flip it early, and never reset back to false
	// once true - so this is NOT "the current load has settled": a status
	// filter re-runs `load()`, and if the new filter excludes the lesson the
	// detail pane has open, `listed` goes null - but LessonDetail mirrors
	// that lesson into its own state the last time it WAS listed, so this
	// staying true lets its fetch guard see that mirrored copy instead of
	// treating the pool as unasked again and re-fetching a lesson it is
	// already showing.
	const [poolSettled, setPoolSettled] = useState(false);
	const [filter, setFilter] = useState<"" | LessonStatus>("");
	// Empty until read, and empty if the read fails: the control then doesn't
	// show, and the page is what it was without one.
	const [projects, setProjects] = useState<Project[]>([]);
	// A name rather than an entry, so a rename that regroups keys can't leave a
	// stale set of keys behind.
	const [selected, setSelected] = useState<string | null>(null);
	// Cleared by `load()` the moment a new query starts, not once it resolves
	// - see the comment there. That still leaves one painted frame between a
	// filter change and the effect that calls `load()` running, because
	// `useEffect` is passive: React commits the `filter` state and the DOM it
	// produces first, THEN runs effects. Closing that fully would mean
	// storing the cursor together with the sequence number that minted it
	// (`useState<{ value: string; seq: number } | null>`) so a stale render
	// could be told apart from a current one without waiting on an effect at
	// all. Considered and left out: one frame is not the hundreds of
	// milliseconds a round-trip takes, and nothing here can be clicked
	// without user input landing inside a real frame to begin with.
	const [cursor, setCursor] = useState<string | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [moreError, setMoreError] = useState<string | null>(null);
	const [ended, setEnded] = useState(false);

	// Which request is newest. Two filter changes inside one round-trip issue
	// two requests, and without this whichever SETTLES last would win rather
	// than whichever was ASKED last - leaving the select reading one status
	// while the list shows another. A stale rejection is worse still: it
	// would blank a list a newer request had already filled.
	const requestSeq = useRef(0);

	const entries = projectEntries(projects, labels);
	const entry = entries.find((option) => option.name === selected) ?? null;
	// A string, so `load` changes when the keys do and not on every render.
	const projectKey = entry ? entryKeys(entry).join("\n") : "";
	const query = useCallback(
		() => ({
			...(filter ? { statuses: [filter] } : {}),
			...(projectKey ? { project: projectKey.split("\n") } : {}),
		}),
		[filter, projectKey],
	);

	// Which pane the narrow layout should show. The layout route does not
	// receive the child's params, so the path is matched directly.
	const detail = useMatch("/lessons/:id");

	const load = useCallback(async () => {
		const seq = ++requestSeq.current;
		setLoadError(null);
		setMoreError(null);
		// A new query starts over: any in-flight loadMore belongs to the query
		// being replaced, and any cursor left over from it names a boundary
		// this query never established. Cleared here, synchronously, rather
		// than waiting for this load to resolve - otherwise the OLD "Load
		// more" button stays mounted and clickable for the whole round-trip,
		// and a click during that window would send the NEW filter paired
		// with the OLD cursor.
		setCursor(null);
		// `loadingMore` clears here too, for the same reason: the OLD
		// loadMore's flight is not THIS query's concern. Without this, a
		// filter change made while a loadMore was outstanding would remount
		// "Load more" - once this query's own page arrives with more to give
		// - already reading "Loading..." and disabled, for a request nobody
		// watching it ever started. It would stay that way until the stale
		// request finally settles, which client.ts's retries can stretch to
		// ~45s. The matching guard lives in loadMore's `finally`, below.
		setLoadingMore(false);
		// And `ended` with them, for the third time in the same breath: a new
		// query has not reached its own end, whatever the last one found. This
		// is the reset that makes an `ended` flag safe here at all - it is why
		// the end-of-pool message cannot be derived from `cursor === null`,
		// which this line also produces on every filter change.
		setEnded(false);
		try {
			const page = await listLessons(query());
			if (seq !== requestSeq.current) return;
			setLessons(page.lessons);
			// `?? []` is not defensive padding: a page without `owned_ids` -
			// an older API still rolling out under a newer bundle - would
			// otherwise throw out of this handler and blank the pool entirely.
			// Empty is also the safe reading, since it offers no status
			// control rather than one that cannot succeed.
			setOwnedIds(page.owned_ids ?? []);
			// `?? {}`, for the identical reason the line above defaults to `[]`:
			// a page without `authors` - an older API still rolling out under a
			// newer bundle - would otherwise throw out of this handler and
			// blank the pool entirely. Empty is also the safe reading, since
			// no page renders a name it cannot attribute.
			setAuthors(page.authors ?? {});
			// `?? {}`, for the identical reason the two lines above default: a
			// page without `retractable_in` - an older API still rolling out
			// under a newer bundle - would otherwise throw out of this handler
			// and blank the pool entirely. Empty is also the safe reading,
			// since it offers no retract control rather than one that cannot
			// succeed.
			setRetractableIn(page.retractable_in ?? {});
			// `?? []`, for the identical reason the lines above default: a page
			// without `former_authors` - an older API still rolling out under a
			// newer bundle - would otherwise throw out of this handler and blank
			// the pool entirely. Empty is also the safe reading, since no page
			// says an author has left when it cannot confirm one has.
			setFormerAuthors(page.former_authors ?? []);
			// `has_more` and not `cursor !== null`, because those are two facts
			// and only one of them is the question being asked - even though,
			// today, they always agree. `listLessonsPage` derives `hasMore` as
			// `rows.length > limit`, which guarantees a defined `last` row
			// whenever it is true, so the pool cannot currently answer
			// `has_more: true` with `cursor: null`. That guarantee is asserted
			// nowhere, though - if it ever broke, this line would silently drop
			// the tail, since reading `page.cursor` here behaves identically to
			// reading `cursor !== null` under exactly the disagreement this
			// comment defends against. The fix belongs where the pairing is
			// produced - an assertion in `listLessonsPage` - not as a client
			// branch defending against a shape the server cannot currently send.
			setCursor(page.has_more ? page.cursor : null);
		} catch (error) {
			if (seq !== requestSeq.current) return;
			setLessons(null);
			setOwnedIds([]);
			setAuthors({});
			setRetractableIn({});
			setFormerAuthors([]);
			setCursor(null);
			setLoadError(describeError(error, "Could not load the pool."));
		} finally {
			// Guarded too: if a superseded request settles while the newest is
			// still in flight, flipping this to true would tell the detail pane
			// the pool is settled while `lessons` is still empty - and it would
			// conclude an id is absent and fetch a lesson that is about to
			// arrive.
			if (seq === requestSeq.current) setPoolSettled(true);
		}
	}, [query]);

	useEffect(() => {
		void load();
	}, [load]);

	// Its own effect, for the labels' reason and one more: a failure here
	// must never blank the pool.
	useEffect(() => {
		let active = true;
		listProjects()
			.then(({ projects: loaded }) => {
				if (active) setProjects(loaded);
			})
			.catch(() => {
				// Deliberately quiet: the page works without the control.
			});
		return () => {
			active = false;
		};
	}, []);

	// Its own effect rather than part of load(), so a filter change does not
	// reread it and a failure here never blanks a pool that loaded fine -
	// SessionsPage keeps the same read apart for the same reason.
	useEffect(() => {
		let active = true;
		listLabels()
			.then((loaded) => {
				if (active) setLabels(loaded);
			})
			.catch(() => {
				// Deliberately quiet: a key shown as itself is not wrong.
			});
		return () => {
			active = false;
		};
	}, []);

	const loadMore = async () => {
		if (!cursor || loadingMore) return;
		// Not `++requestSeq.current`: loadMore continues the query load() most
		// recently started rather than starting a new one, so it reads the
		// current sequence number instead of minting the next one. Comparing
		// against it below is what lets a filter change - which DOES increment
		// this - discard a page that lands after the query it belonged to has
		// already been replaced.
		//
		// Defensive rather than load-bearing now that `load()` clears `cursor`
		// synchronously: the button that starts a loadMore disappears the
		// instant a new query begins, so no loadMore can even be INITIATED
		// while a load() is in flight, and which of the two bumps the counter
		// stops being something either path can observe. Left in place because
		// it is still the semantically correct shape - loadMore continues a
		// query, it does not start one - and because that guarantee lives in
		// render timing, not in this function.
		const seq = requestSeq.current;
		setLoadingMore(true);
		setMoreError(null);
		try {
			// The filter travels with the cursor. A cursor is a position within
			// ONE query's ordering, so paging with a different filter than the
			// one that minted it walks a boundary that query never established.
			// The project is part of that query exactly as the status is.
			const page = await listLessons({ ...query(), cursor });
			if (seq !== requestSeq.current) return;
			setLessons((current) => [...(current ?? []), ...page.lessons]);
			setOwnedIds((current) => [...current, ...(page.owned_ids ?? [])]);
			setAuthors((current) => ({ ...current, ...(page.authors ?? {}) }));
			setRetractableIn((current) => ({
				...current,
				...(page.retractable_in ?? {}),
			}));
			setFormerAuthors((current) => [
				...current,
				...(page.former_authors ?? []),
			]);
			setCursor(page.has_more ? page.cursor : null);
			// Behind the same seq guard as the append itself: a page that lands
			// after the filter has moved on must not report ITS end as the end
			// of the query now on screen.
			setEnded(!page.has_more);
		} catch (error) {
			if (seq !== requestSeq.current) return;
			// The pages already loaded stay. A failed append is a missing tail,
			// not a reason to throw away what the person is reading.
			setMoreError(describeError(error, "Could not load more lessons."));
		} finally {
			// Guarded: `load()` clearing `loadingMore` synchronously (above)
			// means a second loadMore can start - and set this flag true again
			// - while THIS call, the superseded one, is still out there settling
			// late. An unconditional clear here would end that second, genuinely
			// in-flight request's loading state early: re-enabling the button
			// mid-request and risking a duplicate append of the very page it is
			// already fetching.
			if (seq === requestSeq.current) setLoadingMore(false);
		}
	};

	const patchLesson = useCallback((id: string, status: LessonStatus) => {
		setLessons((current) =>
			current === null
				? current
				: current.map((lesson) =>
						lesson.id === id ? { ...lesson, status } : lesson,
					),
		);
	}, []);

	const context: LessonsContext = {
		lessons: lessons ?? [],
		ownedIds,
		poolSettled,
		patchLesson,
		authors,
		retractableIn,
		formerAuthors,
		labels,
	};

	// The live region's whole text, computed once rather than duplicated
	// between the region and whatever branch below happens to be showing -
	// the two can say the same thing (an empty state's title, a count) without
	// being the same JSX, since the region has to exist even when the branch
	// it is summarizing does not render its own heading (the loading paragraph,
	// the row list).
	//
	// The empty title has one source for the same reason: the region and the
	// heading below must not disagree about which empty this is. A project
	// names itself ahead of a status, since it is the narrower claim.
	const emptyTitle = entry
		? `No lessons in ${entry.name}`
		: filter
			? (FILTERS.find((option) => option.value === filter)?.empty ??
				"No lessons")
			: "Nothing has synced yet";
	const resultSummary = loadError
		? "Could not load the pool"
		: lessons === null
			? "Loading the pool…"
			: lessons.length === 0
				? emptyTitle
				: `${lessons.length} lesson${lessons.length === 1 ? "" : "s"}`;

	return (
		<div className="lessons-layout" data-pane={detail ? "detail" : "list"}>
			<div className="lessons-list">
				{entries.length > 0 ? (
					<div style={{ marginBottom: "1rem" }}>
						<ProjectFilter
							entries={entries}
							value={entry?.name ?? null}
							onChange={setSelected}
						/>
					</div>
				) : null}
				<div style={{ marginBottom: "1rem" }}>
					<label
						htmlFor="lesson-status"
						style={{
							display: "flex",
							alignItems: "center",
							gap: "var(--space-1)",
							marginBottom: "0.35rem",
							fontFamily: "var(--font-data)",
							fontSize: "var(--text-data-sm)",
							letterSpacing: "1px",
							textTransform: "uppercase",
							color: PALETTE.muted,
						}}
					>
						<Icon name="MagnifyingGlass" />
						Status
					</label>
					{/*
					  A native select rather than a new form primitive. One
					  filter does not justify a SelectField in form.tsx, and the
					  native control is what a screen reader and a keyboard
					  already know how to drive.
					*/}
					<select
						id="lesson-status"
						value={filter}
						onChange={(event) =>
							setFilter(event.target.value as "" | LessonStatus)
						}
						style={{
							padding: "0.4rem 0.5rem",
							background: "var(--ground)",
							color: "var(--ink)",
							border: `2px solid ${PALETTE.border}`,
							borderRadius: 0,
							fontFamily: "var(--font-body)",
						}}
					>
						{FILTERS.map((option) => (
							<option key={option.value || "all"} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
				{/*
				  One persistent region, present in every branch below, rather
				  than a role appearing only on the branches that need it. A
				  screen reader reliably announces content CHANGING inside a
				  region that is already there; a region that appears at the
				  same moment as its own text is not something NVDA, JAWS and
				  VoiceOver agree on. Panel and its <nav> of rows stay outside
				  this element entirely - the region carries a short summary
				  (a count, or the state's own title), never the rows, so a
				  fifty-row pool does not read every claim aloud on a filter
				  change.
				*/}
				<p
					role="status"
					aria-live="polite"
					style={{
						margin: "0 0 var(--space-2)",
						fontFamily: "var(--font-data)",
						fontSize: "var(--text-data-sm)",
						letterSpacing: "1px",
						textTransform: "uppercase",
						color: PALETTE.muted,
					}}
				>
					{resultSummary}
				</p>
				{loadError ? (
					<EmptyState
						title="Could not load the pool"
						action={{ label: "Retry", onClick: () => void load() }}
					>
						{loadError}
					</EmptyState>
				) : lessons === null ? (
					// Not <Loading>: the region above already carries the role="status"
					// duty for this exact text - resultSummary reads "Loading the
					// pool…" too while lessons is null - so mounting the primitive's
					// own role="status" paragraph here sat a second live region next
					// to the first, both announcing the same words at once. Same
					// hazard the "load more" retirement message reasons about further
					// down this file, just triggered by mount instead of by a click.
					// This mirrors the primitive's look without a second role.
					<p style={{ color: PALETTE.muted }}>{resultSummary}</p>
				) : lessons.length === 0 ? (
					entry || filter ? (
						// An empty FILTER result and an empty POOL say different
						// things. Telling someone whose pool is full to "connect a
						// machine" because they filtered to a status nothing holds,
						// or a project with no lessons, would be a lie, and the kind
						// that makes a person doubt everything else the page says.
						<EmptyState title={emptyTitle}>
							{entry
								? "Nothing you can read in the pool matches that project."
								: "Nothing in the pool holds that status right now."}
						</EmptyState>
					) : (
						<EmptyState
							title="Nothing has synced yet"
							icon="ChestTreasure"
							tone="teal"
						>
							Lessons arrive when a machine pushes them.{" "}
							{/*
							  A link and not EmptyState's action button. The button
							  is for Retry; one that navigated would read as an
							  action and be a link wearing the wrong control.
							*/}
							<NavLink to="/machines" style={{ color: PALETTE.accent }}>
								Connect a machine
							</NavLink>{" "}
							to start.
						</EmptyState>
					)
				) : (
					<Panel title="The pool">
						<nav aria-label="Lessons">
							{lessons.map((lesson) => (
								// NavLink, not Link: it sets aria-current="page" on the
								// selected one, which is the only thing telling a screen
								// reader which row the detail pane is showing.
								<NavLink
									key={lesson.id}
									to={`/lessons/${lesson.id}`}
									style={({ isActive }) => ({
										...row,
										background: isActive ? "var(--panel)" : "transparent",
										borderLeft: isActive
											? `4px solid ${PALETTE.accent}`
											: "4px solid transparent",
									})}
								>
									<Plate
										tone={lesson.status === "active" ? "teal" : "red"}
										icon={STATUS_ICONS[lesson.status]}
									/>
									<span style={{ minWidth: 0, flex: 1 }}>
										<span
											style={{
												display: "block",
												marginBottom: "var(--space-1)",
												fontSize: "var(--text-body-md)",
											}}
										>
											{lesson.claim}
										</span>
										<span
											style={{
												display: "flex",
												gap: "var(--space-2)",
												alignItems: "center",
												flexWrap: "wrap",
											}}
										>
											<StatusBadge status={lesson.status} />
											{lesson.applies_to.stack[0] ? (
												<Chip>{lesson.applies_to.stack[0]}</Chip>
											) : null}
											{authors[lesson.id] ? (
												<span data-testid="lesson-author">
													{authors[lesson.id]}
												</span>
											) : null}
											<When
												iso={lesson.promoted_at}
												style={{
													color: PALETTE.muted,
													fontSize: "var(--text-body-sm)",
												}}
											/>
										</span>
									</span>
								</NavLink>
							))}
						</nav>

						<LoadMore
							cursor={cursor}
							loading={loadingMore}
							ended={ended}
							endLabel="That's the whole pool."
							error={moreError}
							onLoadMore={() => void loadMore()}
							style={{ marginTop: "1rem" }}
						/>
					</Panel>
				)}
			</div>

			<div className="lessons-detail">
				{detail ? (
					<Outlet context={context} />
				) : (
					<Panel>
						<p style={{ margin: 0, color: PALETTE.muted }}>
							Select a lesson to read its rationale and evidence.
						</p>
					</Panel>
				)}
			</div>
		</div>
	);
}
